import { ReactNode, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AllocationFragment,
  ExpensableBudgetFragment,
  ExpensablePerDiemFragment,
  ExpenseDetailPageQuery,
  SpendingAlertFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import ConfirmationScreen from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/perdiem'
import EditExpenseFooterActions from 'modules/expenses/components/expenses/Detail/_Form/Common/EditExpenseFooterActions'
import SnackBarAlerts from 'modules/expenses/components/expenses/Detail/_Form/Common/SnackBarAlerts'
import { type LocalConcept } from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/DailyConcepts'
import { useBulkActions } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import {
  normalizeEffectiveOn,
  useDelete,
  useSendForApproval,
  useUpdate,
  useUpdateAndApprove,
} from 'modules/expenses/hooks/useExpensable'
import { useExpenseAlerts } from 'modules/expenses/hooks/useExpenseAlerts'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import useNewExpensesPage from 'modules/expenses/hooks/useNewExpensesPage'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import {
  isApprovedExpensable,
  isDraftExpensable,
  isManualExpensable,
  isNewPerDiem,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

import NewPerDiemForm from './new'
import { getPerDiemFormDefaultValues } from './new/form/form'
import { PerDiemSchema } from './new/form/schema'

import { ScopedEditSection } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

type ViewMode =
  | { type: 'confirmation' }
  | { type: 'fullEdit'; scrollTo?: string }
  | { type: 'sectionEdit'; section: ScopedEditSection }

const SCOPED_EDIT_SECTION_VALUES: ScopedEditSection[] = [
  'tripInformation',
  'companyPaid',
  'basicInformation',
  'budgetAndProject',
  'costCenters',
  'additionalInformation',
]

const SCOPED_EDIT_SECTIONS: ReadonlySet<string> = new Set(SCOPED_EDIT_SECTION_VALUES)

const isScopedEditSection = (section: string): section is ScopedEditSection =>
  SCOPED_EDIT_SECTIONS.has(section)

interface PerDiemFormProps {
  expensable: Expensable
  perDiem: ExpensablePerDiemFragment
  customizationFields: CustomizationField[]
  editable: boolean
  approvalsInformation?: ReactNode
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  canOverrideApprovalFlow: boolean
  canReviewExpensable: boolean
  canSetExpensableToPaid: boolean
  canSeeEmployeeLegalEntity: boolean
  canDeleteExpensable: boolean
  onClickSnackBar: () => void
  toggleAlert: () => void
  hideAlert: boolean
  canUseSpendingPolicies: boolean
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicyEvaluationResult?: SpendingPolicesResultData | null
  spendingAlerts?: SpendingAlertFragment[]
  hasProjectsFeature: boolean
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  approvalsV2Enabled: boolean
  approvalProcess:
    | ExpenseDetailPageQuery['expenses']['expensablesConnection']['edges'][0]['node']['approvalProcess']
    | null
}

const PerDiemForm = ({
  expensable,
  perDiem: _perDiem,
  customizationFields,
  editable,
  approvalsInformation,
  onClose,
  hasReportOnBehalfFeature,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  canOverrideApprovalFlow,
  canReviewExpensable,
  canSetExpensableToPaid,
  canSeeEmployeeLegalEntity,
  canDeleteExpensable,
  onClickSnackBar,
  toggleAlert,
  hideAlert,
  canUseSpendingPolicies,
  budget,
  budgetAllocation,
  spendingPolicyEvaluationResult,
  spendingAlerts,
  hasProjectsFeature,
  registerDropHandler,
  approvalsV2Enabled,
  approvalProcess,
}: PerDiemFormProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { canCreateExpensesForOthers } = useNewExpensesPage()
  const newPerDiem = isNewPerDiem(expensable)

  const bulkActions = useBulkActions()
  const { loading: updating } = useUpdate()
  const { loading: updatingAndApproving } = useUpdateAndApprove()
  const { loading: sendingForApproval } = useSendForApproval()
  const { loading: deleting } = useDelete()
  const submitting = updating || updatingAndApproving || sendingForApproval || deleting

  const isDraft = isDraftExpensable(expensable)

  const isDraftManual = expensable && isManualExpensable(expensable) && isDraft
  const syncableState = expensable?.syncableState?.[0] ?? null

  const { alertMessage, alertType } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: Number(expensable.amount),
    reimbursableAmount: null, // no reimbursable amount on per diems
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    spendingAlerts,
    syncableState,
  })

  const showSnackBar =
    alertMessage.length > 0 &&
    !hideAlert &&
    canUseSpendingPolicies &&
    expensable &&
    (isDraft || isPendingExpensable(expensable) || isApprovedExpensable(expensable))

  const defaultValues = getPerDiemFormDefaultValues({
    employee: currentEmployee,
    customizationFields,
    groupId: expensable?.groupId,
    expensable,
    isNewPerDiem: newPerDiem,
  })

  const [formEmployeeId, setFormEmployeeId] = useState<string | null | undefined>(
    defaultValues.employee?.id
  )
  const [hasModifiedConcepts, setHasModifiedConcepts] = useState(false)
  const [modifiedConceptsData, setModifiedConceptsData] = useState<LocalConcept[] | null>(null)

  const { employee } = useCurrentSession()
  const employeeId = formEmployeeId ?? employee?.id
  // TODO: Implement the effective on change based on per diem

  const effectiveOn = new Date().toISOString() // TBD, Dummy data

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: hasProjectsFeature === false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(effectiveOn),
    employeeId: employeeId,
    skip: false,
  })

  const perDiemForm = useForm({
    mode: 'onChange',
    resolver: zodResolver(
      PerDiemSchema({
        customizationFields,
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
        resourceType: 'perdiem',
        isNewPerDiem: newPerDiem,
      })
    ),
    defaultValues: defaultValues,
  })

  const { trigger } = perDiemForm
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'fullEdit' })
  const [hasValidatedInitially, setHasValidatedInitially] = useState(false)

  // Reset modified concepts state when expensable changes
  useUnsafeEffect(() => {
    setHasModifiedConcepts(false)
    setModifiedConceptsData(null)
  }, [expensable?.id])

  // On mount, validate the form to decide whether to show confirmation or edit
  useUnsafeEffect(() => {
    if (hasValidatedInitially) return
    trigger().then((validationResult) => {
      if (validationResult) {
        setViewMode({ type: 'confirmation' })
      }
      // else stays on fullEdit
      setHasValidatedInitially(true)
    })
  }, [customizationFields, trigger, hasValidatedInitially])

  return (
    <Form padding="s0" notRounded>
      {
        <Box gap="s32" paddingX="s24">
          <FormProvider {...perDiemForm}>
            {viewMode.type === 'confirmation' ? (
              <ConfirmationScreen
                expensable={expensable}
                customizationFields={customizationFields}
                resourceType="perdiem"
                onEdit={(scrollToSection?: string | undefined) => {
                  if (newPerDiem && scrollToSection && isScopedEditSection(scrollToSection)) {
                    setViewMode({ type: 'sectionEdit', section: scrollToSection })
                  } else {
                    setViewMode({ type: 'fullEdit', scrollTo: scrollToSection })
                  }
                }}
                editable={editable}
                isNewPerDiem={newPerDiem}
              />
            ) : (
              <NewPerDiemForm
                customizationFields={customizationFields}
                canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                canCreateExpensesForOthers={canCreateExpensesForOthers}
                canEditExpensesWithPendingStatus={editable}
                expensable={expensable}
                hasProjectsFeature={hasProjectsFeature}
                hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                taxAgencyOrganization={null}
                projectWorkers={newProjectWorkers}
                budgetOptions={newBudgetOptions}
                loadingProjectWorkers={newLoadingProjectWorkers}
                loadingBudgetOptions={newLoadingBudgetOptions}
                onEmployeeIdChange={setFormEmployeeId}
                onEffectiveOnChange={() => {}}
                registerDropHandler={registerDropHandler}
                scrollTo={viewMode.type === 'fullEdit' ? viewMode.scrollTo : undefined}
                isNewPerDiem={newPerDiem}
                onConceptsModified={(concepts) => {
                  setHasModifiedConcepts(true)
                  setModifiedConceptsData(concepts)
                }}
                editSection={viewMode.type === 'sectionEdit' ? viewMode.section : undefined}
                onBackToConfirmation={() => setViewMode({ type: 'confirmation' })}
              />
            )}
          </FormProvider>

          {!submitting && !bulkActions.submitting && !isDraft && approvalsInformation}
        </Box>
      }
      <SnackBarAlerts
        showSnackBar={showSnackBar}
        alertType={alertType}
        alertMessage={alertMessage}
        toggleAlert={toggleAlert}
        onClickLink={onClickSnackBar}
      />
      <EditExpenseFooterActions
        formType="perdiem"
        expensable={expensable}
        formValues={perDiemForm.getValues()}
        customizationFields={customizationFields}
        canEdit={editable}
        canOverrideApprovalFlow={canOverrideApprovalFlow}
        canSetExpensableToPaid={canSetExpensableToPaid}
        canReviewExpensable={canReviewExpensable}
        canDeleteExpensable={canDeleteExpensable}
        reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
        reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
        taxAgencyOrganization={null}
        formValid={perDiemForm.formState.isValid}
        formDirty={perDiemForm.formState.isDirty || hasModifiedConcepts}
        trigger={perDiemForm.trigger}
        onClose={onClose}
        approvalsV2Enabled={approvalsV2Enabled}
        approvalProcess={approvalProcess}
        isNewPerDiem={newPerDiem}
        modifiedConcepts={modifiedConceptsData}
      />
    </Form>
  )
}
export default observer(PerDiemForm)
